import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Search, Send, Plus, User, MessageSquare, Check, CheckCheck,
  Paperclip, X, FileText, Image, File, Download, Loader2, Globe, Bot, Languages
} from "lucide-react";
import { format } from "date-fns";
import AIPalChat from "@/components/AIPalChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGE_NAMES = {
  "en": "English",
  "fr": "French",
  "es": "Spanish",
  "pt": "Portuguese",
  "zh": "Mandarin",
  "ar": "Arabic",
  "de": "German",
  "it": "Italian",
  "ru": "Russian",
  "ja": "Japanese"
};

export default function Chat() {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserLang, setCurrentUserLang] = useState("en");
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showChatpal, setShowChatpal] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchConversations();
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (activeConversation) {
      fetchMessages(activeConversation.id);
    }
  }, [activeConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // WebSocket connection for real-time messages
  useEffect(() => {
    if (!currentUserId) return;

    const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsProtocol = baseUrl.startsWith("https") ? "wss" : "ws";
    const wsBase = baseUrl.replace(/^https?:\/\//, "");
    const wsUrl = `${wsProtocol}://${wsBase}/ws/comms/${currentUserId}`;

    let reconnectTimer = null;

    const connect = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        ws._pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "new_message") {
            if (data.conversation_id === activeConversation?.id) {
              setMessages((prev) => [...prev, data.message]);
            }
            fetchConversations();
          }
        } catch {}
      };

      ws.onclose = () => {
        clearInterval(ws._pingInterval);
        wsRef.current = null;
        reconnectTimer = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      clearTimeout(reconnectTimer);
      if (wsRef.current) {
        clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [currentUserId, activeConversation?.id]);

  const fetchCurrentUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setCurrentUserId(res.data.user_id);
      setCurrentUserLang(res.data.default_language || "en");
    } catch (err) {
      console.error("Failed to fetch user");
    }
  };

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/comms/chat/conversations`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setConversations(res.data);
    } catch (err) {
      toast.error("Failed to load conversations");
    }
  };

  const fetchMessages = async (convId) => {
    try {
      const res = await axios.get(`${API}/comms/chat/conversations/${convId}/messages`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setMessages(res.data);
      
      // Mark as read
      await axios.post(`${API}/comms/chat/conversations/${convId}/read`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
    } catch (err) {
      toast.error("Failed to load messages");
    }
  };

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const res = await axios.get(`${API}/comms/chat/users/search?q=${encodeURIComponent(query)}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setSearchResults(res.data);
    } catch (err) {
      toast.error("Search failed");
    } finally {
      setIsSearching(false);
    }
  };

  const startConversation = async (userId) => {
    try {
      const res = await axios.post(`${API}/comms/chat/conversations`, 
        { user_id: userId },
        { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true }
      );
      setActiveConversation(res.data);
      setSearchQuery("");
      setSearchResults([]);
      fetchConversations();
    } catch (err) {
      toast.error("Failed to start conversation");
    }
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && attachments.length === 0) || !activeConversation) return;
    
    try {
      const res = await axios.post(
        `${API}/comms/chat/conversations/${activeConversation.id}/messages`,
        { content: newMessage, attachments: attachments },
        { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true }
      );
      setMessages([...messages, res.data]);
      setNewMessage("");
      setAttachments([]);
      fetchConversations();
    } catch (err) {
      toast.error("Failed to send message");
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setIsUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        
        const res = await axios.post(`${API}/comms/chat/upload`, formData, {
          headers: getHeaders(),
          withCredentials: true
        });
        
        setAttachments(prev => [...prev, res.data]);
      }
      toast.success(`${files.length} file(s) attached`);
    } catch (err) {
      toast.error("Failed to upload file");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const getFileIcon = (type) => {
    if (type?.startsWith("image/")) return Image;
    if (type?.includes("pdf") || type?.includes("document")) return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <header className="border-b border-app bg-app-card px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-app">
            DIRECT MESSAGES
          </h1>
          <p className="text-sm text-app-secondary">Private conversations</p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={currentUserLang}
            onValueChange={async (v) => {
              setCurrentUserLang(v);
              try {
                await axios.put(`${API}/auth/language`, { language: v }, {
                  headers: getHeaders(), withCredentials: true,
                });
              } catch {}
            }}
          >
            <SelectTrigger className="h-9 w-36 text-xs rounded-none border-app">
              <Languages className="w-3.5 h-3.5 text-app-secondary" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-app-card rounded-none max-h-60">
              {Object.entries(LANGUAGE_NAMES).map(([code, name]) => (
                <SelectItem key={code} value={code} className="text-sm">{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => setShowChatpal(!showChatpal)}
            variant={showChatpal ? "default" : "outline"}
            className={showChatpal ? "bg-violet-500 hover:bg-violet-600" : ""}
            data-testid="chatpal-toggle"
          >
            <Bot className="w-4 h-4 mr-2" />
            Chatpal
          </Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Conversation List */}
        <div className="w-80 border-r border-app bg-app-card flex flex-col">
          {/* Search */}
          <div className="p-4 border-b border-app">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-app-secondary" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  searchUsers(e.target.value);
                }}
                className="pl-9 bg-app border-app rounded-lg"
                data-testid="user-search-input"
              />
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-2 bg-app-card border border-app rounded-lg shadow-lg max-h-48 overflow-auto">
                {searchResults.map((user) => (
                  <button
                    key={user.user_id}
                    onClick={() => startConversation(user.user_id)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-app text-left"
                    data-testid={`user-result-${user.user_id}`}
                  >
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-violet-100 text-violet-600 text-xs">
                        {getInitials(user.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm text-app">{user.name}</p>
                      <p className="text-xs text-app-secondary">{user.email}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Conversations */}
          <ScrollArea className="flex-1">
            {conversations.length === 0 ? (
              <div className="p-6 text-center">
                <MessageSquare className="w-12 h-12 text-app-secondary mx-auto mb-3" />
                <p className="text-app-secondary text-sm">No conversations yet</p>
                <p className="text-app-secondary text-xs">Search for users to start chatting</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => setActiveConversation(conv)}
                  className={`w-full flex items-center gap-3 p-4 border-b border-app hover:bg-app text-left transition-colors ${
                    activeConversation?.id === conv.id ? 'bg-violet-50' : ''
                  }`}
                  data-testid={`conversation-${conv.id}`}
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={conv.other_user?.picture} />
                    <AvatarFallback className="bg-violet-100 text-violet-600">
                      {getInitials(conv.other_user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-app truncate">
                        {conv.other_user?.name || "Unknown User"}
                      </p>
                      {conv.unread_count > 0 && (
                        <span className="bg-violet-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-app-secondary truncate">
                      {conv.last_message?.content || "No messages yet"}
                    </p>
                  </div>
                </button>
              ))
            )}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-app">
          {activeConversation ? (
            <>
              {/* Chat Header */}
              <div className="bg-app-card border-b border-app p-4 flex items-center gap-3">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={activeConversation.other_user?.picture} />
                  <AvatarFallback className="bg-violet-100 text-violet-600">
                    {getInitials(activeConversation.other_user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-app">
                    {activeConversation.other_user?.name}
                  </p>
                  <p className="text-xs text-app-secondary">
                    {activeConversation.other_user?.email}
                  </p>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map((msg) => {
                    const isOwn = msg.sender_id === currentUserId;
                    const isTranslated = !isOwn && msg.original_language && msg.original_language !== currentUserLang;
                    const originalLangName = LANGUAGE_NAMES[msg.original_language] || msg.original_language;
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                            isOwn
                              ? 'bg-violet-500 text-white rounded-br-md'
                              : 'bg-app-card border border-app text-app rounded-bl-md'
                          }`}
                        >
                          {/* Translation indicator */}
                          {isTranslated && (
                            <div className="flex items-center gap-1 text-xs text-amber-600 mb-1 pb-1 border-b border-app">
                              <Globe className="w-3 h-3" />
                              <span>Received in {originalLangName}</span>
                            </div>
                          )}
                          
                          {msg.content && <p className="text-sm">{isOwn ? msg.content : (msg.translated_content || msg.content)}</p>}
                          
                          {/* Attachments */}
                          {msg.attachments && msg.attachments.length > 0 && (
                            <div className={`space-y-2 ${msg.content ? 'mt-2' : ''}`}>
                              {msg.attachments.map((att, idx) => {
                                const FileIcon = getFileIcon(att.type);
                                const isImage = att.type?.startsWith("image/");
                                return (
                                  <div key={idx}>
                                    {isImage ? (
                                      <a 
                                        href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block"
                                      >
                                        <img 
                                          src={`${process.env.REACT_APP_BACKEND_URL}${att.url}`}
                                          alt={att.name}
                                          className="max-w-full rounded-lg max-h-48 object-cover"
                                        />
                                      </a>
                                    ) : (
                                      <a
                                        href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex items-center gap-2 p-2 rounded-lg ${
                                          isOwn ? 'bg-violet-400/30' : 'bg-slate-100'
                                        }`}
                                      >
                                        <FileIcon className="w-4 h-4 flex-shrink-0" />
                                        <div className="min-w-0 flex-1">
                                          <p className="text-xs font-medium truncate">{att.name}</p>
                                          <p className={`text-xs ${isOwn ? 'text-violet-200' : 'text-app-secondary'}`}>
                                            {formatFileSize(att.size)}
                                          </p>
                                        </div>
                                        <Download className="w-4 h-4 flex-shrink-0" />
                                      </a>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                            <span className={`text-xs ${isOwn ? 'text-violet-200' : 'text-app-secondary'}`}>
                              {format(new Date(msg.created_at), 'HH:mm')}
                            </span>
                            {isOwn && (
                              msg.read_by?.length > 1 
                                ? <CheckCheck className="w-3 h-3 text-violet-200" />
                                : <Check className="w-3 h-3 text-violet-200" />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Message Input */}
              <div className="bg-app-card border-t border-app p-4">
                {/* Attachment Preview */}
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {attachments.map((att) => {
                      const FileIcon = getFileIcon(att.type);
                      return (
                        <div 
                          key={att.id}
                          className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2"
                        >
                          <FileIcon className="w-4 h-4 text-app-secondary" />
                          <span className="text-sm text-app max-w-[150px] truncate">{att.name}</span>
                          <button
                            onClick={() => removeAttachment(att.id)}
                            className="text-app-secondary hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    multiple
                    data-testid="file-input"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="rounded-full w-10 h-10 p-0"
                    data-testid="attach-file-btn"
                  >
                    {isUploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Paperclip className="w-4 h-4" />
                    )}
                  </Button>
                  <Input
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    className="flex-1 bg-app border-app rounded-full bg-surface-input text-app placeholder:text-placeholder"
                    data-testid="message-input"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() && attachments.length === 0}
                    className="rounded-full w-10 h-10 p-0 bg-violet-500 hover:bg-violet-600"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 text-app-secondary mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-app">Select a conversation</h3>
                <p className="text-app-secondary text-sm">or search for a user to start chatting</p>
              </div>
            </div>
          )}
        </div>

        {/* Chatpal Panel */}
        {showChatpal && (
          <div className="w-96 border-l border-app">
            <AIPalChat
              palId="chatpal"
              palName="Chatpal"
              palIcon={MessageSquare}
              color="from-violet-500 to-purple-500"
              onClose={() => setShowChatpal(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
